import { ReactNode, useCallback, useMemo } from 'react';
import { renderRoutes, matchRoutes, RouteConfig } from 'react-router-config';
import { connect } from 'react-redux';
import { RouterState } from 'redux-first-history';

import MainLayout from 'common/layouts/MainLayout';
import SimpleLayout from 'common/layouts/SimpleLayout';
// eslint-disable-next-line local-rules/no-import-from-other-directory
import OfflinePage from 'common/pages/OfflinePage';
import { isMobileRootSelector, isOnLineSelector } from 'common/selectors/ui';
import { useT } from 'common/i18n';
import { isOnlineCheckedAction, UIState } from 'common/reducers/ui';

type LayoutSwitcherProps = {
  pathname: string;
  sidebar: ReactNode;
  header: ReactNode;
  isOnLineChecked: (isOnline: boolean) => void;
  isOnline: boolean;
  isMobile: boolean;
  mainLayoutRoutes: RouteConfig[];
  simpleLayoutRoutes: RouteConfig[];
};

const LayoutSwitcher = ({
  mainLayoutRoutes,
  simpleLayoutRoutes,
  pathname,
  sidebar,
  header,
  isOnLineChecked,
  isOnline,
  isMobile,
}: LayoutSwitcherProps) => {
  const { routes, Layout } = useMemo(
    () =>
      [{ routes: mainLayoutRoutes, Layout: MainLayout }].find(
        ({ routes }) => matchRoutes(routes, pathname).length > 0,
      ) || { routes: simpleLayoutRoutes, Layout: SimpleLayout },
    [pathname, mainLayoutRoutes, simpleLayoutRoutes],
  );

  const handleRetryClick = useCallback(() => {
    isOnLineChecked(navigator.onLine);
  }, [isOnLineChecked]);

  const offlineTranslation = useT('sidebar.offlinePage');

  return (
    <Layout sidebar={sidebar} header={header}>
      {isOnline ? (
        renderRoutes(routes)
      ) : (
        <OfflinePage
          offlineTranslation={offlineTranslation}
          onRetryClick={handleRetryClick}
          isMobile={isMobile}
        />
      )}
    </Layout>
  );
};

const mapStateToProps = (state: { ui: UIState; router: RouterState }) => ({
  pathname: state.router.location!.pathname,
  isOnline: isOnLineSelector(state),
  isMobile: isMobileRootSelector(state),
});

export default connect(mapStateToProps, {
  isOnLineChecked: isOnlineCheckedAction,
})(LayoutSwitcher);
